import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../../lib/supabase-auth';
import { PortalLayout } from '../../../../components/portal/PortalLayout';
import { useAuth } from '../../../../contexts/AuthContextLazy';
import ClientSelector from '../../../../components/compliance/ClientSelector';
import WizardSteps from '../../../../components/compliance/WizardSteps';
import { showWarning, showInfo, showSuccess, showError } from '../../../../utils/toast';

interface ReasonWhyData {
  client_info: {
    name: string;
    email: string;
    address?: string;
  };
  advisor_info: {
    name: string;
    title: string;
    license_number: string;
  };
  needs_analysis_summary: {
    date: string;
    key_findings: string[];
    financial_situation: string;
    insurance_gap: number;
  };
  recommendations: {
    products: Array<{
      type: string;
      carrier: string;
      product_name: string;
      coverage_amount: number;
      premium: number;
      features: string[];
      reason_for_recommendation: string;
    }>;
    total_coverage: number;
    total_premium: number;
  };
  compliance_elements: {
    needs_addressed: boolean;
    alternatives_discussed: boolean;
    suitability_confirmed: boolean;
    cost_disclosure: boolean;
    risks_disclosed: boolean;
  };
  additional_notes: string;
  signatures_required: Array<{
    party_id: string;
    party_name: string;
    party_type: 'client' | 'spouse' | 'advisor';
    signed: boolean;
  }>;
}

const WIZARD_STEPS = [
  { id: 'setup', name: 'Setup' },
  { id: 'analysis', name: 'Needs Analysis Review' },
  { id: 'recommendations', name: 'Product Recommendations' },
  { id: 'compliance', name: 'Compliance Check' },
  { id: 'preview', name: 'Preview & Sign' }
];

export default function ReasonWhyBuilder() {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [needsAnalysis, setNeedsAnalysis] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentRecord, setDocumentRecord] = useState<any>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [dataChanged, setDataChanged] = useState(false);
  
  const preselectedClientId = searchParams.get('clientId');
  const needsAnalysisId = searchParams.get('needsAnalysisId');

  const [documentData, setDocumentData] = useState<ReasonWhyData>({
    client_info: {
      name: '',
      email: ''
    },
    advisor_info: {
      name: user?.user_metadata?.full_name || '',
      title: 'Financial Advisor',
      license_number: ''
    },
    needs_analysis_summary: {
      date: '',
      key_findings: [],
      financial_situation: '',
      insurance_gap: 0
    },
    recommendations: {
      products: [],
      total_coverage: 0,
      total_premium: 0
    },
    compliance_elements: {
      needs_addressed: false,
      alternatives_discussed: false,
      suitability_confirmed: false,
      cost_disclosure: false,
      risks_disclosed: false
    },
    additional_notes: '',
    signatures_required: []
  });

  useEffect(() => {
    if (documentId) {
      loadExistingDocument();
    } else if (preselectedClientId) {
      loadPreselectedClient();
    }
  }, [documentId, preselectedClientId]);

  useEffect(() => {
    if (needsAnalysisId) {
      loadNeedsAnalysis();
    }
  }, [needsAnalysisId]);

  // Auto-save functionality
  useEffect(() => {
    if (!autoSaveEnabled || !dataChanged || !selectedClient) return;

    const saveTimer = setTimeout(async () => {
      localStorage.setItem('reason_why_draft', JSON.stringify({
        clientId: selectedClient.id,
        data: documentData,
        timestamp: new Date().toISOString()
      }));

      await saveProgress();
      setDataChanged(false);
      setLastSaved(new Date());
    }, 30000); // 30 seconds

    return () => clearTimeout(saveTimer);
  }, [documentData, dataChanged, autoSaveEnabled, selectedClient]);

  const loadPreselectedClient = async () => {
    if (!preselectedClientId) return;
    
    try {
      const { data: client, error } = await supabase
        .from('parties')
        .select('id, name, primary_email')
        .eq('id', preselectedClientId)
        .single();
        
      if (error) throw error;
      
      if (client) {
        setSelectedClient(client);
        updateField('client_info.name', client.name);
        updateField('client_info.email', client.primary_email);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading pre-selected client:', error);
      }
    }
  };

  const loadNeedsAnalysis = async () => {
    if (!needsAnalysisId) return;

    try {
      const { data: doc, error } = await supabase
        .from('compliance_documents')
        .select(`
          *,
          current_version:document_versions!compliance_documents_current_version_id_fkey(
            id,
            content,
            created_at
          )
        `)
        .eq('id', needsAnalysisId)
        .eq('document_type', 'needs_analysis')
        .single();

      if (error) throw error;

      if (doc && doc.current_version) {
        setNeedsAnalysis(doc);
        populateFromNeedsAnalysis(doc.current_version.content, doc.current_version.created_at);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading needs analysis:', error);
      }
      showError('Failed to load needs analysis');
    }
  };

  const populateFromNeedsAnalysis = (needsData: any, createdAt: string) => {
    // Extract key information from needs analysis
    const income = needsData.financial?.annual_income || 0;
    const spouseIncome = needsData.financial?.spouse_income || 0;
    const totalIncome = income + spouseIncome;
    const currentCoverage = needsData.insurance?.life_policies?.reduce(
      (sum: number, policy: any) => sum + (policy.coverage || 0), 0
    ) || 0;
    
    // Calculate insurance gap using DIME method
    const debtAndLiabilities = needsData.financial?.total_liabilities || 0;
    const incomeReplacement = totalIncome * 10;
    const mortgageBalance = debtAndLiabilities * 0.7; // Assume 70% is mortgage
    const educationCosts = (needsData.family?.dependents?.length || 0) * 100000;
    
    const totalNeed = debtAndLiabilities + incomeReplacement + mortgageBalance + educationCosts;
    const insuranceGap = Math.max(0, totalNeed - currentCoverage);

    // Key findings
    const keyFindings = [];
    if (needsData.family?.dependents?.length > 0) {
      keyFindings.push(`${needsData.family.dependents.length} dependent(s) requiring financial protection`);
    }
    if (insuranceGap > 0) {
      keyFindings.push(`Insurance shortfall of $${insuranceGap.toLocaleString()}`);
    }
    if (needsData.goals?.education_funding_needed) {
      keyFindings.push('Education funding needed for dependents');
    }
    if (needsData.goals?.estate_planning_concerns) {
      keyFindings.push('Estate planning concerns identified');
    }

    // Update document data
    setDocumentData(prev => ({
      ...prev,
      needs_analysis_summary: {
        date: createdAt,
        key_findings: keyFindings,
        financial_situation: `Annual household income of $${totalIncome.toLocaleString()} with current life insurance coverage of $${currentCoverage.toLocaleString()}.`,
        insurance_gap: insuranceGap
      }
    }));
  };

  const loadExistingDocument = async () => {
    try {
      setLoading(true);
      const { data: doc, error } = await supabase
        .from('compliance_documents')
        .select(`
          *,
          client:parties(id, name, primary_email),
          current_version:document_versions!compliance_documents_current_version_id_fkey(
            id,
            content,
            version_number
          )
        `)
        .eq('id', documentId)
        .single();

      if (error) throw error;

      if (doc) {
        setDocumentRecord(doc);
        setSelectedClient(doc.client);
        if (doc.current_version?.content) {
          setDocumentData(doc.current_version.content);
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading document:', error);
      }
      showError('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (path: string, value: any) => {
    const keys = path.split('.');
    setDocumentData((prev) => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!(keys[i] in current)) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    setDataChanged(true);
  };

  const saveProgress = async () => {
    if (!selectedClient) return;

    try {
      setSaving(true);

      if (documentRecord) {
        // Update existing document
        const { data: version, error: versionError } = await supabase
          .rpc('create_document_version', {
            p_document_id: documentRecord.id,
            p_content: documentData,
            p_created_by: user?.id,
            p_change_summary: 'Progress saved'
          });

        if (versionError) throw versionError;
      } else {
        // Create new document
        const { data: doc, error: docError } = await supabase
          .from('compliance_documents')
          .insert({
            client_id: selectedClient.id,
            advisor_id: user?.id,
            document_type: 'reason_why',
            status: 'draft'
          })
          .select()
          .single();

        if (docError) throw docError;

        // Create first version
        const { data: version, error: versionError } = await supabase
          .from('document_versions')
          .insert({
            document_id: doc.id,
            version_number: 1,
            is_current: true,
            content: documentData,
            created_by: user?.id
          })
          .select()
          .single();

        if (versionError) throw versionError;

        // Update document with current version
        await supabase
          .from('compliance_documents')
          .update({ current_version_id: version.id })
          .eq('id', doc.id);

        setDocumentRecord(doc);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error saving progress:', error);
      }
      showError('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const addProduct = () => {
    const newProduct = {
      type: 'term',
      carrier: '',
      product_name: '',
      coverage_amount: 0,
      premium: 0,
      features: [],
      reason_for_recommendation: ''
    };

    setDocumentData(prev => ({
      ...prev,
      recommendations: {
        ...prev.recommendations,
        products: [...prev.recommendations.products, newProduct]
      }
    }));
    setDataChanged(true);
  };

  const updateProduct = (index: number, field: string, value: any) => {
    setDocumentData(prev => {
      const newProducts = [...prev.recommendations.products];
      newProducts[index] = {
        ...newProducts[index],
        [field]: value
      };

      // Recalculate totals
      const totalCoverage = newProducts.reduce((sum, p) => sum + (p.coverage_amount || 0), 0);
      const totalPremium = newProducts.reduce((sum, p) => sum + (p.premium || 0), 0);

      return {
        ...prev,
        recommendations: {
          ...prev.recommendations,
          products: newProducts,
          total_coverage: totalCoverage,
          total_premium: totalPremium
        }
      };
    });
    setDataChanged(true);
  };

  const removeProduct = (index: number) => {
    setDocumentData(prev => {
      const newProducts = prev.recommendations.products.filter((_, i) => i !== index);
      
      // Recalculate totals
      const totalCoverage = newProducts.reduce((sum, p) => sum + (p.coverage_amount || 0), 0);
      const totalPremium = newProducts.reduce((sum, p) => sum + (p.premium || 0), 0);

      return {
        ...prev,
        recommendations: {
          ...prev.recommendations,
          products: newProducts,
          total_coverage: totalCoverage,
          total_premium: totalPremium
        }
      };
    });
    setDataChanged(true);
  };

  const handleNext = async () => {
    if (currentStep === 0 && !selectedClient) {
      showWarning('Please select a client');
      return;
    }

    // Save progress before moving to next step
    await saveProgress();

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Setup
        return (
          <div className="space-y-6">
            <ClientSelector
              onSelectClient={setSelectedClient}
              documentType="reason_why"
            />
            
            {selectedClient && (
              <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Link to Needs Analysis</h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Select a completed needs analysis to auto-populate recommendation details.
                  </p>
                  <button
                    onClick={() => {
                      // TODO: Show needs analysis selector
                      showInfo('Needs analysis selector coming soon');
                    }}
                    className="w-full text-left p-4 border border-gray-300 rounded-lg hover:border-amber-500 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">Select Needs Analysis</p>
                        <p className="text-sm text-gray-500">Auto-populate from existing analysis</p>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 1: // Needs Analysis Review
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Needs Analysis Summary</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Analysis Date
                </label>
                <input
                  type="date"
                  value={documentData.needs_analysis_summary.date?.split('T')[0] || ''}
                  onChange={(e) => updateField('needs_analysis_summary.date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Financial Situation Summary
                </label>
                <textarea
                  value={documentData.needs_analysis_summary.financial_situation}
                  onChange={(e) => updateField('needs_analysis_summary.financial_situation', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  placeholder="Describe the client's current financial situation..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Findings
                </label>
                <div className="space-y-2">
                  {documentData.needs_analysis_summary.key_findings.map((finding, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={finding}
                        onChange={(e) => {
                          const newFindings = [...documentData.needs_analysis_summary.key_findings];
                          newFindings[index] = e.target.value;
                          updateField('needs_analysis_summary.key_findings', newFindings);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      />
                      <button
                        onClick={() => {
                          const newFindings = documentData.needs_analysis_summary.key_findings.filter((_, i) => i !== index);
                          updateField('needs_analysis_summary.key_findings', newFindings);
                        }}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newFindings = [...documentData.needs_analysis_summary.key_findings, ''];
                      updateField('needs_analysis_summary.key_findings', newFindings);
                    }}
                    className="text-amber-600 hover:text-amber-700 font-medium"
                  >
                    + Add Finding
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Insurance Coverage Gap
                </label>
                <div className="flex items-center">
                  <span className="mr-2">$</span>
                  <input
                    type="number"
                    value={documentData.needs_analysis_summary.insurance_gap}
                    onChange={(e) => updateField('needs_analysis_summary.insurance_gap', parseFloat(e.target.value) || 0)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Product Recommendations
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Product Recommendations</h2>
            
            <div className="space-y-6">
              {documentData.recommendations.products.map((product, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-medium text-gray-900">Product {index + 1}</h3>
                    <button
                      onClick={() => removeProduct(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Type
                      </label>
                      <select
                        value={product.type}
                        onChange={(e) => updateProduct(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      >
                        <option value="term">Term Life</option>
                        <option value="whole">Whole Life</option>
                        <option value="universal">Universal Life</option>
                        <option value="disability">Disability</option>
                        <option value="critical_illness">Critical Illness</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Insurance Carrier
                      </label>
                      <input
                        type="text"
                        value={product.carrier}
                        onChange={(e) => updateProduct(index, 'carrier', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        placeholder="e.g., Manulife"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={product.product_name}
                        onChange={(e) => updateProduct(index, 'product_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                        placeholder="e.g., Term 20"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Coverage Amount
                      </label>
                      <input
                        type="number"
                        value={product.coverage_amount}
                        onChange={(e) => updateProduct(index, 'coverage_amount', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Premium
                      </label>
                      <input
                        type="number"
                        value={product.premium}
                        onChange={(e) => updateProduct(index, 'premium', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Recommendation
                    </label>
                    <textarea
                      value={product.reason_for_recommendation}
                      onChange={(e) => updateProduct(index, 'reason_for_recommendation', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 bg-white"
                      placeholder="Explain why this product is suitable for the client..."
                    />
                  </div>
                </div>
              ))}

              <button
                onClick={addProduct}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-amber-500 hover:text-amber-600 transition-colors"
              >
                + Add Product Recommendation
              </button>

              {/* Totals Summary */}
              {documentData.recommendations.products.length > 0 && (
                <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-900 mb-2">Recommendation Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-amber-700">Total Coverage:</span>
                      <span className="font-medium text-amber-900">
                        ${documentData.recommendations.total_coverage.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-700">Total Annual Premium:</span>
                      <span className="font-medium text-amber-900">
                        ${documentData.recommendations.total_premium.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-amber-200">
                      <span className="text-amber-700">Coverage Gap Addressed:</span>
                      <span className="font-medium text-amber-900">
                        {documentData.needs_analysis_summary.insurance_gap > 0
                          ? `${Math.round((documentData.recommendations.total_coverage / documentData.needs_analysis_summary.insurance_gap) * 100)}%`
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 3: // Compliance Check
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Compliance Checklist</h2>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Confirm that all required elements are included in the recommendation letter.
              </p>

              {Object.entries({
                needs_addressed: 'Client needs and objectives have been clearly addressed',
                alternatives_discussed: 'Alternative products and options have been discussed',
                suitability_confirmed: 'Product suitability has been confirmed based on client circumstances',
                cost_disclosure: 'All costs, fees, and charges have been disclosed',
                risks_disclosed: 'Potential risks and limitations have been explained'
              }).map(([key, label]) => (
                <label key={key} className="flex items-start">
                  <input
                    type="checkbox"
                    checked={documentData.compliance_elements[key as keyof typeof documentData.compliance_elements]}
                    onChange={(e) => updateField(`compliance_elements.${key}`, e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="ml-3 text-sm text-gray-700">{label}</span>
                </label>
              ))}

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes or Disclosures
                </label>
                <textarea
                  value={documentData.additional_notes}
                  onChange={(e) => updateField('additional_notes', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-amber-500 focus:border-amber-500 text-gray-900 bg-white"
                  placeholder="Any additional information that should be included..."
                />
              </div>

              {/* Compliance Status */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  {Object.values(documentData.compliance_elements).every(v => v) ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      <span className="text-green-700 font-medium">All compliance requirements met</span>
                    </>
                  ) : (
                    <>
                      <div className="h-5 w-5 rounded-full border-2 border-amber-500 mr-2" />
                      <span className="text-amber-700">
                        {Object.values(documentData.compliance_elements).filter(v => v).length} of 5 requirements checked
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Preview & Sign
        return (
          <div className="bg-white portal-content-shadow rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Preview Reason Why Letter</h2>
            
            <div className="prose max-w-none">
              <div className="border-2 border-gray-200 rounded-lg p-8 bg-gray-50">
                {/* Letter Preview */}
                <div className="text-right mb-8 text-gray-600">
                  {new Date().toLocaleDateString('en-CA', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>

                <div className="mb-8">
                  <p className="font-semibold">{documentData.client_info.name}</p>
                  {documentData.client_info.address && (
                    <p className="text-gray-600">{documentData.client_info.address}</p>
                  )}
                </div>

                <p className="mb-4">Dear {documentData.client_info.name},</p>

                <p className="mb-4">
                  RE: <strong>Insurance Recommendations - Reason Why Letter</strong>
                </p>

                <p className="mb-4">
                  Thank you for taking the time to meet with me to discuss your insurance needs. 
                  Based on our comprehensive needs analysis completed on{' '}
                  {documentData.needs_analysis_summary.date
                    ? new Date(documentData.needs_analysis_summary.date).toLocaleDateString('en-CA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : '[Date]'}, 
                  I am pleased to provide you with my recommendations.
                </p>

                <h3 className="font-semibold mb-2">Your Current Situation</h3>
                <p className="mb-4">{documentData.needs_analysis_summary.financial_situation}</p>

                {documentData.needs_analysis_summary.key_findings.length > 0 && (
                  <>
                    <h3 className="font-semibold mb-2">Key Findings</h3>
                    <ul className="list-disc list-inside mb-4">
                      {documentData.needs_analysis_summary.key_findings.map((finding, index) => (
                        <li key={index}>{finding}</li>
                      ))}
                    </ul>
                  </>
                )}

                {documentData.needs_analysis_summary.insurance_gap > 0 && (
                  <p className="mb-4">
                    Based on our analysis, you currently have an insurance coverage gap of{' '}
                    <strong>${documentData.needs_analysis_summary.insurance_gap.toLocaleString()}</strong>.
                  </p>
                )}

                <h3 className="font-semibold mb-2">My Recommendations</h3>
                {documentData.recommendations.products.map((product, index) => (
                  <div key={index} className="mb-4">
                    <p className="font-medium">
                      {index + 1}. {product.carrier} - {product.product_name}
                    </p>
                    <ul className="list-disc list-inside ml-4 text-gray-700">
                      <li>Coverage Amount: ${product.coverage_amount.toLocaleString()}</li>
                      <li>Annual Premium: ${product.premium.toLocaleString()}</li>
                    </ul>
                    <p className="mt-2 text-gray-700">{product.reason_for_recommendation}</p>
                  </div>
                ))}

                {documentData.recommendations.products.length > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 rounded border border-amber-200">
                    <p className="font-medium">Total Recommended Coverage: ${documentData.recommendations.total_coverage.toLocaleString()}</p>
                    <p>Total Annual Premium: ${documentData.recommendations.total_premium.toLocaleString()}</p>
                  </div>
                )}

                {documentData.additional_notes && (
                  <>
                    <h3 className="font-semibold mb-2 mt-6">Additional Information</h3>
                    <p className="mb-4">{documentData.additional_notes}</p>
                  </>
                )}

                <p className="mt-8 mb-4">
                  If you have any questions about these recommendations, please don't hesitate to contact me.
                </p>

                <p className="mb-8">Sincerely,</p>

                <div className="mt-16">
                  <p className="font-semibold">{documentData.advisor_info.name}</p>
                  <p className="text-gray-600">{documentData.advisor_info.title}</p>
                  {documentData.advisor_info.license_number && (
                    <p className="text-gray-600">License #: {documentData.advisor_info.license_number}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Signature Setup */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature Requirements</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="rounded border-gray-300 text-amber-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Client signature required ({documentData.client_info.name})
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-amber-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Spouse signature required (if applicable)
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="rounded border-gray-300 text-amber-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Advisor signature required ({documentData.advisor_info.name})
                  </span>
                </label>
              </div>

              <button
                onClick={() => {
                  showInfo('E-signing integration coming soon');
                }}
                className="mt-6 w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors"
              >
                Send for E-Signature
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/portal/compliance')}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Reason Why Letter</h1>
              <p className="text-gray-600 mt-1">
                {selectedClient ? `For ${selectedClient.name}` : 'Select a client to begin'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoSaveEnabled}
                onChange={(e) => setAutoSaveEnabled(e.target.checked)}
                className="mr-2 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Auto-save
            </label>
            {saving && (
              <span className="text-sm text-gray-500 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500 mr-2"></div>
                Saving...
              </span>
            )}
            {lastSaved && !saving && (
              <span className="text-sm text-gray-500">
                Last saved: {lastSaved.toLocaleTimeString()} ✓
              </span>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <WizardSteps
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />

        {/* Step Content */}
        <div className="mt-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`flex items-center px-4 py-2 rounded-lg font-medium ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Previous
          </button>

          <button
            onClick={handleNext}
            className="flex items-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium"
          >
            {currentStep === WIZARD_STEPS.length - 1 ? 'Complete' : 'Next'}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>
      </div>
    </PortalLayout>
  );
}